export type McpOAuthEnvironmentKind = 'production' | 'staging' | 'unsupported';

export interface SupportedMcpOAuthEnvironment {
  claudeCodeCommand: string;
  kind: 'production' | 'staging';
  /** Canonical Streamable HTTP MCP endpoint shown in Settings. */
  mcpUrl: string;
  /** Deprecated server alias; not shown in Settings UI. */
  sseUrl: string;
}

export interface UnsupportedMcpOAuthEnvironment {
  kind: 'unsupported';
}

export type McpOAuthEnvironment =
  | SupportedMcpOAuthEnvironment
  | UnsupportedMcpOAuthEnvironment;

const PRODUCTION_MCP_URL = 'https://mcp.heysavvy.io/mcp';
const STAGING_MCP_URL = 'https://staging-mcp.heysavvy.io/mcp';

const toLegacySseUrl = (mcpUrl: string): string =>
  mcpUrl.replace(/\/mcp$/, '/sse');

const buildSupportedEnvironment = (
  kind: 'production' | 'staging',
  mcpUrl: string,
): SupportedMcpOAuthEnvironment => ({
  claudeCodeCommand: buildClaudeCodeCommand(mcpUrl),
  kind,
  mcpUrl,
  sseUrl: toLegacySseUrl(mcpUrl),
});

export const buildClaudeCodeCommand = (
  mcpUrl: string,
  serverAlias = 'savvy',
): string => `claude mcp add --transport http ${serverAlias} ${mcpUrl}`;

export const isSupportedMcpOAuthEnvironment = (
  environment: McpOAuthEnvironment,
): environment is SupportedMcpOAuthEnvironment =>
  environment.kind !== 'unsupported';

export const resolveMcpOAuthEnvironment = (
  hostname: string = window.location.hostname,
): McpOAuthEnvironment => {
  if (hostname === 'app.heysavvy.io') {
    return buildSupportedEnvironment('production', PRODUCTION_MCP_URL);
  }

  if (hostname === 'staging-app.heysavvy.io') {
    return buildSupportedEnvironment('staging', STAGING_MCP_URL);
  }

  return { kind: 'unsupported' };
};

export const resolveMcpOAuthDisplayEnvironment = (
  hostname: string = window.location.hostname,
): SupportedMcpOAuthEnvironment => {
  const environment = resolveMcpOAuthEnvironment(hostname);

  if (isSupportedMcpOAuthEnvironment(environment)) {
    return environment;
  }

  return buildSupportedEnvironment('staging', STAGING_MCP_URL);
};
